import type {
  Charge,
  ChargeFilter,
  ChargeStatus,
  ConsoleDataPort,
  Customer,
  CustomerQuery,
  DeadJob,
  DunningLadder,
  DunningPreview,
  DunningStep,
  GatewayInfo,
  OptOut,
  ProviderConfigView,
  ProviderSummary,
  ProviderTestResult,
  QueueSummary,
  ServiceHealth,
} from './port.js';

/**
 * Adapter HTTP — o console lendo o banco de VERDADE.
 *
 * Substitui o `InMemoryConsoleData`. A diferenca visivel e que agora o console
 * mostra o que existe, e hoje quase nada existe: o sistema ainda nao emitiu
 * cobranca nenhuma. Tela vazia e o estado honesto, e ela para de ser vazia
 * sozinha quando os dados chegarem.
 *
 * O cookie de sessao acompanha toda chamada (`credentials: 'include'`). Sem
 * isso o servidor devolve 401 mesmo com o operador logado.
 */

/** O backend fala o dominio; a tela fala portugues. A traducao mora aqui. */
const STATUS_PARA_TELA: Record<string, ChargeStatus> = {
  PENDING: 'PENDENTE',
  PAID: 'PAGO',
  SETTLED: 'LIQUIDADO',
  EXPIRED: 'EXPIRADO',
  FAILED: 'FALHOU',
};

const STATUS_PARA_API: Record<ChargeStatus, string> = {
  PENDENTE: 'PENDING',
  PAGO: 'PAID',
  LIQUIDADO: 'SETTLED',
  EXPIRADO: 'EXPIRED',
  FALHOU: 'FAILED',
};

/**
 * Erro de acao que o backend ainda nao oferece.
 *
 * Existe para o comando FALHAR ALTO em vez de fingir. Um `addStep` que nao faz
 * nada e responde "ok" e pior que um erro: o operador acredita que configurou
 * a regua e vai embora.
 */
export class AcaoIndisponivelError extends Error {
  constructor(oQueFalta: string) {
    super(oQueFalta);
    this.name = 'AcaoIndisponivelError';
  }
}

interface RespostaApi<T> {
  success: boolean;
  data: T;
  error: { code: string; message: string } | null;
}

/** Cobranca como o backend devolve — campos do SGP nao vem aqui. */
interface CobrancaDaApi {
  invoiceId: string;
  gatewayTxId: string | null;
  amountCents: number;
  status: string;
  link: string | null;
  /** Retrato do título no SGP, gravado na emissão. `null` em cobranças antigas. */
  customerName?: string | null;
  contract?: string | null;
  taxId?: string | null;
  dueDate?: string | null;
}

export interface HttpConsoleDataOpcoes {
  baseUrl: string;
  /** Injetavel para teste. */
  fetchImpl?: typeof fetch;
}

export class HttpConsoleData implements ConsoleDataPort {
  private readonly baseUrl: string;
  private readonly buscar: typeof fetch;

  constructor(opcoes: HttpConsoleDataOpcoes) {
    this.baseUrl = opcoes.baseUrl.replace(/\/+$/, '');
    this.buscar = opcoes.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  private async chamar<T>(rota: string, init: RequestInit = {}): Promise<T> {
    const resposta = await this.buscar(`${this.baseUrl}${rota}`, {
      ...init,
      credentials: 'include',
      headers: { 'content-type': 'application/json', ...init.headers },
    });

    const corpo = (await resposta.json().catch(() => null)) as RespostaApi<T> | null;

    if (!resposta.ok) {
      // A mensagem do servidor e melhor que qualquer texto generico: ela sabe
      // se foi papel insuficiente, filtro invalido ou registro inexistente.
      throw new Error(corpo?.error?.message ?? `o middleware respondeu ${resposta.status}`);
    }
    return corpo!.data;
  }

  /**
   * Traduz a cobranca do backend para o que a tela espera.
   *
   * Os quatro campos do SGP saem `null`, nao string vazia: a tela mostra um
   * travessao, que se le como "isto vem do ERP e ainda nao temos" — diferente
   * de vazio, que se le como "o cliente nao tem nome".
   */
  private paraTela(bruta: CobrancaDaApi): Charge {
    return {
      invoiceId: bruta.invoiceId,
      contract: bruta.contract ?? null,
      customerName: bruta.customerName ?? null,
      taxId: bruta.taxId ?? null,
      dueDate: bruta.dueDate ?? null,
      // O backend chama de `gatewayTxId`; a tela, de `end2end`. Sao o mesmo
      // valor com nomes que nasceram em lados diferentes do sistema.
      end2end: bruta.gatewayTxId,
      amountCents: bruta.amountCents,
      status: STATUS_PARA_TELA[bruta.status] ?? 'FALHOU',
      link: bruta.link,
    };
  }

  async listCharges(filter: ChargeFilter): Promise<Charge[]> {
    const params = new URLSearchParams();
    if (filter.status) params.set('status', STATUS_PARA_API[filter.status]);
    if (filter.limit) params.set('limit', String(filter.limit));

    const lista = await this.chamar<CobrancaDaApi[]>(`/console/charges?${params.toString()}`);
    return lista.map((bruta) => this.paraTela(bruta));
  }

  async findCharge(invoiceId: string): Promise<Charge | null> {
    try {
      return this.paraTela(
        await this.chamar<CobrancaDaApi>(`/console/charges/${encodeURIComponent(invoiceId)}`),
      );
    } catch {
      // 404 e resposta legitima: o titulo simplesmente nao tem cobranca.
      return null;
    }
  }

  async createCharge(input: {
    invoiceId: string;
    payerName?: string;
    payerDocument?: string;
  }): Promise<Charge> {
    // Rota propria do console, autenticada por SESSAO. O `POST /charges` exige
    // `x-api-key`, que o navegador nao tem e nao pode ter.
    const criada = await this.chamar<CobrancaDaApi>('/console/charges', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return this.paraTela(criada);
  }

  async findCustomers(query: CustomerQuery): Promise<Customer[]> {
    const params = new URLSearchParams();
    if (query.clienteId) params.set('clienteId', query.clienteId);
    if (query.contract) params.set('contrato', query.contract);
    if (query.taxId) params.set('cpf', query.taxId);
    if (query.name) params.set('nome', query.name);

    const lista = await this.chamar<
      Array<{
        name: string;
        taxId: string | null;
        contracts: string[];
        phonesE164: string[];
        invoices: Array<{
          invoiceId: string;
          amountCents: number;
          dueDate: string | null;
          status: string;
          contract: string | null;
          daysOverdue: number | null;
        }>;
      }>
    >(`/console/customers?${params.toString()}`);

    return lista.map((cliente) => ({
      name: cliente.name,
      taxId: cliente.taxId ?? null,
      contracts: cliente.contracts ?? [],
      phones: cliente.phonesE164 ?? [],
      invoices: (cliente.invoices ?? []).map((titulo) => ({
        invoiceId: titulo.invoiceId,
        amountCents: titulo.amountCents,
        dueDate: titulo.dueDate ?? null,
        status: titulo.status,
        contract: titulo.contract ?? null,
        daysOverdue: titulo.daysOverdue ?? null,
      })),
    }));
  }

  async getQueue(): Promise<QueueSummary> {
    return this.chamar<QueueSummary>('/console/queue');
  }

  async listDeadJobs(): Promise<DeadJob[]> {
    const lista = await this.chamar<
      Array<{
        type: string;
        key: string;
        attempts: number;
        lastError: string | null;
        failedAt: string;
      }>
    >('/console/queue/dead?limit=50');

    return lista.map((job) => ({
      end2end: job.key,
      type: job.type,
      attempts: job.attempts,
      lastError: job.lastError ?? 'sem detalhe',
      failedAt: job.failedAt,
    }));
  }

  async requeue(end2end: string): Promise<boolean> {
    try {
      // A chave de baixa e o `gatewayTxId`; a de webhook e `invoiceId:stepId`.
      // O `:` distingue os dois sem precisar perguntar ao operador.
      const type = end2end.includes(':') ? 'DISPATCH_WEBHOOK' : 'SETTLE_INVOICE';
      await this.chamar('/console/queue/requeue', {
        method: 'POST',
        body: JSON.stringify({ type, key: end2end }),
      });
      return true;
    } catch {
      return false;
    }
  }

  async listOptOuts(): Promise<OptOut[]> {
    return this.chamar<OptOut[]>('/console/optouts?limit=100');
  }

  async addOptOut(taxId: string, reason?: string): Promise<boolean> {
    await this.chamar('/console/optouts', {
      method: 'POST',
      body: JSON.stringify({ taxId, ...(reason ? { reason } : {}) }),
    });
    return true;
  }

  async removeOptOut(taxId: string): Promise<boolean> {
    try {
      await this.chamar(`/console/optouts/${encodeURIComponent(taxId)}`, { method: 'DELETE' });
      return true;
    } catch {
      return false;
    }
  }

  async getLadder(): Promise<DunningLadder> {
    const regua = await this.chamar<{
      name: string;
      active: boolean;
      dailyCap: number | null;
      steps: Array<{ id: number; offsetDays: number; label: string; active: boolean }>;
    } | null>('/console/ladder');

    if (!regua) {
      // Nenhuma regua cadastrada e estado legitimo — devolve uma vazia em vez
      // de estourar, para `regua ver` conseguir dizer "nenhuma configurada".
      return { name: '(nenhuma configurada)', active: false, dailyCap: 0, steps: [] };
    }

    return {
      name: regua.name,
      active: regua.active,
      // `null` = a regua nao tem teto proprio e usa o do ambiente. A tela
      // mostra 0, que se le como "sem limite definido aqui".
      dailyCap: regua.dailyCap ?? 0,
      steps: regua.steps.map((etapa) => ({
        id: etapa.id,
        offsetDays: etapa.offsetDays,
        label: etapa.label,
        active: etapa.active,
      })),
    };
  }

  private async reguaApos(corpo: Record<string, unknown>): Promise<DunningLadder> {
    await this.chamar('/console/ladder', { method: 'PATCH', body: JSON.stringify(corpo) });
    // Recarrega em vez de confiar no retorno: a tela mostra o estado
    // consolidado, incluindo as etapas, que o PATCH nao devolve.
    return this.getLadder();
  }

  async setLadderActive(active: boolean): Promise<DunningLadder> {
    return this.reguaApos({ active });
  }

  async setDailyCap(cap: number): Promise<DunningLadder> {
    return this.reguaApos({ dailyCap: cap });
  }

  async addStep(input: { offsetDays: number; label: string }): Promise<DunningStep> {
    return this.chamar<DunningStep>('/console/ladder/steps', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async removeStep(stepId: number): Promise<boolean> {
    try {
      await this.chamar(`/console/ladder/steps/${stepId}`, { method: 'DELETE' });
      return true;
    } catch {
      return false;
    }
  }

  async setStepActive(stepId: number, active: boolean): Promise<DunningStep | null> {
    try {
      return await this.chamar<DunningStep>(`/console/ladder/steps/${stepId}`, {
        method: 'PATCH',
        body: JSON.stringify({ active }),
      });
    } catch {
      return null;
    }
  }

  previewDunning(_date: string): Promise<DunningPreview> {
    return Promise.reject(
      new AcaoIndisponivelError(
        'Simular a régua varre os títulos do SGP, e a credencial configurada é de teste.',
      ),
    );
  }

  async getGateway(): Promise<GatewayInfo> {
    return this.chamar<GatewayInfo>('/console/gateway');
  }

  async setGateway(name: string): Promise<GatewayInfo> {
    // O POST devolve so `{ active }`; recarrega para a tela mostrar tambem os
    // disponiveis e o default, como no `gateway ver`.
    await this.chamar('/console/gateway', { method: 'POST', body: JSON.stringify({ name }) });
    return this.getGateway();
  }

  async listProviders(): Promise<ProviderSummary[]> {
    return this.chamar<ProviderSummary[]>('/console/providers');
  }

  async getProvider(name: string): Promise<ProviderConfigView> {
    return this.chamar<ProviderConfigView>(`/console/providers/${encodeURIComponent(name)}`);
  }

  async setProviderConfig(name: string, patch: Record<string, string>): Promise<ProviderConfigView> {
    return this.chamar<ProviderConfigView>(`/console/providers/${encodeURIComponent(name)}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  }

  async testProvider(name: string): Promise<ProviderTestResult> {
    return this.chamar<ProviderTestResult>(
      `/console/providers/${encodeURIComponent(name)}/test`,
      { method: 'POST' },
    );
  }

  async health(): Promise<ServiceHealth[]> {
    return this.chamar<ServiceHealth[]>('/console/health');
  }
}
